import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Globe2, Flag, Trophy, BookMarked, ChevronRight, MapPin } from "lucide-react";
import api from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { ThemedShell, ThemedCard, ThemedProgress } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";

const CONTINENT_EMOJI = {
  "Europa": "🏰", "Azië": "🐼", "Afrika": "🦁",
  "Noord-Amerika": "🗽", "Zuid-Amerika": "🌴", "Oceanië": "🏝️",
};

export default function Wereldreis() {
  const navigate = useNavigate();
  const { theme, terms } = useTheme();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/child/world/data").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const muted = { color: "var(--t-muted)" };
  const go = (path, state) => { sfx.select(); navigate(path, { state }); };

  return (
    <ThemedShell>
      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 85%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="world-back-btn"><ArrowLeft className="w-5 h-5 mr-1" /> {terms.dashboard}</Button>
          {data && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "var(--t-surface2)" }} data-testid="world-passport-count">
              <BookMarked className="w-4 h-4" style={{ color: "var(--t-accent)" }} /><span className="font-heading font-extrabold">{data.passport_count}/{data.total_countries}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pt-8 pb-16">
        <div className="flex items-center gap-4 mb-8">
          <motion.div animate={{ rotate: [0, 12, -12, 0] }} transition={{ repeat: Infinity, duration: 6 }} className="w-16 h-16 rounded-3xl flex items-center justify-center text-4xl" style={{ background: "var(--t-surface2)" }}>🌍</motion.div>
          <div>
            <h1 className="font-heading text-3xl font-black themed-heading">Wereldreis</h1>
            <p className="font-semibold" style={muted}>Ontdek landen, vlaggen en hoofdsteden — en vul je wereldpaspoort!</p>
          </div>
        </div>

        {/* Spellen */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <ThemedCard className="p-6 flex flex-col" data-testid="game-flagquiz">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Flag className="w-7 h-7" strokeWidth={2.5} /></div>
            <h3 className="font-heading text-xl font-extrabold themed-heading">Raad de vlag</h3>
            <p className="text-sm font-semibold mb-4 flex-1" style={muted}>Snelronde tegen de klok: van welk land is deze vlag?</p>
            <Button onClick={() => go("/leerling/wereldreis/vlaggen", { wk: false })} className="rounded-full font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="start-flagquiz-btn">Speel <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </ThemedCard>

          <ThemedCard className="p-6 flex flex-col" data-testid="game-wk">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 text-3xl" style={{ background: "var(--t-surface2)" }}>🏆</div>
            <h3 className="font-heading text-xl font-extrabold themed-heading">WK-poule</h3>
            <p className="text-sm font-semibold mb-4 flex-1" style={muted}>Leer alle WK-landen kennen. {data && <b style={{ color: "var(--t-accent)" }}>{data.wk.collected}/{data.wk.total}</b>} verzameld.</p>
            <Button onClick={() => go("/leerling/wereldreis/vlaggen", { wk: true })} className="rounded-full font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="start-wk-btn">Doe mee <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </ThemedCard>

          <ThemedCard className="p-6 flex flex-col" data-testid="game-passport">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 text-3xl" style={{ background: "var(--t-surface2)" }}>📔</div>
            <h3 className="font-heading text-xl font-extrabold themed-heading">Wereldpaspoort</h3>
            <p className="text-sm font-semibold mb-4 flex-1" style={muted}>Bekijk alle landen die je al hebt verzameld, met vlag en hoofdstad.</p>
            <Button onClick={() => go("/leerling/paspoort")} className="rounded-full font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="open-passport-btn">Open <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </ThemedCard>
        </div>

        {/* Wereldkaart per werelddeel */}
        <div className="flex items-center gap-2 mb-3">
          <Globe2 className="w-5 h-5" style={{ color: "var(--t-accent)" }} />
          <p className="font-heading font-bold themed-heading">Wereldkaart — sleep de vlaggen naar het juiste land</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {!data ? (
            [...Array(6)].map((_, i) => <div key={i} className="h-28 rounded-3xl animate-pulse" style={{ background: "var(--t-surface2)" }} />)
          ) : data.continents.map((c) => {
            const pct = c.total ? Math.round((c.collected / c.total) * 100) : 0;
            return (
              <button key={c.name} onClick={() => go("/leerling/wereldreis/kaart", { continent: c.name })} data-testid={`continent-${c.name}`}
                className="text-left rounded-3xl border-2 p-5 transition-all hover:-translate-y-1 active:scale-[0.99]" style={{ background: "var(--t-surface)", borderColor: "var(--t-border)" }}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{CONTINENT_EMOJI[c.name] || "🌍"}</span>
                  <div className="flex-1">
                    <h4 className="font-heading font-extrabold">{c.name}</h4>
                    <p className="text-xs font-semibold flex items-center gap-1" style={muted}><MapPin className="w-3 h-3" /> {c.collected}/{c.total} landen</p>
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: "var(--t-accent)" }} />
                </div>
                <ThemedProgress value={pct} />
              </button>
            );
          })}
        </div>
      </main>
    </ThemedShell>
  );
}
